import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Plus, X, Eye, MapPin, Calendar, Users, Briefcase, FileText, Download, Phone, Mail, User, GraduationCap, AlertCircle, AlertTriangle, Edit, Save } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { getBackendUrl, getAssetUrl, formatSalary } from '@/utils/api';

// Job Type Options
const JOB_TYPES = ['Trainee', 'On-Roll', 'Off-Roll', 'Permanent', 'Probation'];

const JobManagement = () => {
  const navigate = useNavigate();
  const user = getUser();
  const isHR = user?.role === 'hr';
  const [jobs, setJobs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showApplicantsDialog, setShowApplicantsDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const locationInputRef = useRef(null);
  
  // Profile completion state
  const [profileCompleted, setProfileCompleted] = useState(true);
  const [profileMessage, setProfileMessage] = useState('');
  const [missingFields, setMissingFields] = useState([]);
  
  const [formData, setFormData] = useState({
    job_type: 'Permanent',
    job_title: '',
    short_info: '',
    description: '',
    gender: [],
    locations: [],
    skills: [],
    min_experience: 0,
    max_experience: 0,
    min_age: '',
    max_age: '',
    min_education: [],
    course_subject: '',
    min_salary: '',
    max_salary: '',
    salary_type: 'numeric', // 'numeric', 'as_per_norms', 'as_per_experience'
    vacancies: 1,
    last_date: '',
    contact_number: '',
    email: '',
    publish_on: ['job_portal']  // Default: Job Portal always selected
  });
  const [skillInput, setSkillInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);

  const educationOptions = ['10th', '12th', 'ITI', 'Diploma', 'UG Degree', 'PG Degree', 'UG Engineering', 'PG Engineering', 'Others'];

  const handleEducationChange = (education, checked) => {
    if (checked) {
      setFormData({ ...formData, min_education: [...formData.min_education, education] });
    } else {
      setFormData({ ...formData, min_education: formData.min_education.filter(e => e !== education) });
    }
  };

  useEffect(() => {
    fetchJobs();
    checkProfileStatus();
  }, []);

  // Check if employer profile is complete
  const checkProfileStatus = async () => {
    try {
      const employerId = isHR ? user.employer_id : user.id;
      const response = await api.get(`/employer/profile-status/${employerId}`);
      setProfileCompleted(response.data.profile_completed);
      setProfileMessage(response.data.message || '');
      setMissingFields(response.data.missing_fields || []);
    } catch (error) {
      console.error('Error checking profile status:', error);
    }
  };

  // Handle Post New Job button click
  const handlePostNewJob = () => {
    if (!profileCompleted) {
      toast.error('Please complete your company profile before posting jobs.', {
        description: profileMessage,
        duration: 5000,
        action: {
          label: 'Go to Profile',
          onClick: () => navigate('/employer/profile')
        }
      });
      return;
    }
    // Reset form for new job
    setIsEditing(false);
    setEditingJobId(null);
    setFormData({
      job_type: 'Permanent',
      job_title: '',
      short_info: '',
      description: '',
      gender: [],
      locations: [],
      skills: [],
      min_experience: 0,
      max_experience: 0,
      min_age: '',
      max_age: '',
      min_education: [],
      course_subject: '',
      min_salary: '',
      max_salary: '',
      salary_type: 'numeric',
      vacancies: 1,
      last_date: '',
      contact_number: '',
      email: '',
      publish_on: ['job_portal']
    });
    setShowDialog(true);
  };

  // Handle Edit Job
  const handleEditJob = (job) => {
    setIsEditing(true);
    setEditingJobId(job.id);
    setFormData({
      job_type: job.job_type || 'Permanent',
      job_title: job.job_title || '',
      short_info: job.short_info || '',
      description: job.description || '',
      gender: job.gender || [],
      locations: job.locations || [],
      skills: job.skills || [],
      min_experience: job.min_experience || 0,
      max_experience: job.max_experience || 0,
      min_age: job.min_age || '',
      max_age: job.max_age || '',
      min_education: Array.isArray(job.min_education) ? job.min_education : (job.min_education ? [job.min_education] : []),
      course_subject: job.course_subject || '',
      min_salary: job.min_salary || '',
      max_salary: job.max_salary || '',
      salary_type: job.salary_type || 'numeric',
      vacancies: job.vacancies || 1,
      last_date: job.last_date || '',
      contact_number: job.contact_number || '',
      email: job.email || '',
      publish_on: job.publish_on || ['job_portal']
    });
    setShowDialog(true);
  };

  const fetchJobs = async () => {
    try {
      const employerId = isHR ? user.employer_id : user.id;
      const url = isHR 
        ? `/employer/jobs/${employerId}?hr_id=${user.id}`
        : `/employer/jobs/${employerId}`;
      const response = await api.get(url);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchApplicants = async (job) => {
    setLoadingApplicants(true);
    try {
      // Use job-specific endpoint to get applications with proper profile data
      const response = await api.get(`/job/${job.id}/applications`);
      setApplicants(response.data);
      setSelectedJob(job);
      setShowApplicantsDialog(true);
    } catch (error) {
      toast.error('Failed to load applicants');
    } finally {
      setLoadingApplicants(false);
    }
  };

  const validateForm = () => {
    if (!formData.job_type) {
      toast.error('Job Type is required');
      return false;
    }
    if (!formData.job_title) {
      toast.error('Job Designation is required');
      return false;
    }
    if (!formData.short_info) {
      toast.error('Short info about job is required');
      return false;
    }
    if (!formData.description) {
      toast.error('Roles and Responsibilities is required');
      return false;
    }
    if (formData.gender.length === 0) {
      toast.error('Please select at least one gender');
      return false;
    }
    if (formData.locations.length === 0) {
      toast.error('Please add at least one job location');
      return false;
    }
    if (formData.min_education.length === 0) {
      toast.error('Please select at least one qualification');
      return false;
    }
    if (formData.min_experience === '' || formData.min_experience === null) {
      toast.error('Min Experience is required');
      return false;
    }
    if (formData.max_experience === '' || formData.max_experience === null) {
      toast.error('Max Experience is required');
      return false;
    }
    // Skills are now mandatory
    if (!formData.skills || formData.skills.length === 0) {
      toast.error('At least one skill is required');
      return false;
    }
    // Salary validation - min_salary is always required
    if (!formData.min_salary && formData.min_salary !== 0) {
      toast.error('Minimum salary is required');
      return false;
    }
    if (formData.salary_type === 'numeric' && (!formData.max_salary && formData.max_salary !== 0)) {
      toast.error('Maximum salary is required when selecting "Enter Amount"');
      return false;
    }
    if (!formData.vacancies) {
      toast.error('Number of Vacancies is required');
      return false;
    }
    if (!formData.last_date) {
      toast.error('Last Date to Apply is required');
      return false;
    }
    // Either contact number or email is mandatory
    if (!formData.contact_number && !formData.email) {
      toast.error('Either Contact Number or Email is required');
      return false;
    }
    // Validate contact number is exactly 10 digits if provided
    if (formData.contact_number && formData.contact_number.length !== 10) {
      toast.error('Contact Number must be exactly 10 digits');
      return false;
    }
    // Validate contact number starts with 6-9 (Indian mobile)
    if (formData.contact_number && !/^[6-9]/.test(formData.contact_number)) {
      toast.error('Contact Number must start with 6, 7, 8 or 9');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      const employerId = isHR ? user.employer_id : user.id;
      
      // Prepare salary values based on salary_type
      let minSalary = formData.min_salary || 0;
      let maxSalary = formData.salary_type === 'numeric' ? (formData.max_salary || 0) : 0;
      
      const jobData = {
        ...formData,
        min_salary: minSalary,
        max_salary: maxSalary,
        salary_type: formData.salary_type,
        min_education: Array.isArray(formData.min_education) ? formData.min_education : [formData.min_education],
        employer_id: employerId,
        posted_by_id: user.id,
        locations: formData.locations.length > 0 ? formData.locations : [{ city: 'Multiple', state: 'India', district: '' }]
      };
      
      // Check if we're editing an existing job
      if (isEditing && editingJobId) {
        await api.put(`/employer/jobs/${editingJobId}`, jobData);
        toast.success('Job updated successfully! Awaiting admin approval.');
      } else {
        await api.post('/employer/jobs', jobData);
        toast.success('Job posted successfully! Awaiting admin approval.');
      }
      
      setShowDialog(false);
      setIsEditing(false);
      setEditingJobId(null);
      setSelectedJob(null);
      resetForm();
      fetchJobs();
    } catch (error) {
      if (error.response?.data?.detail?.includes('profile')) {
        toast.error(error.response.data.detail, {
          action: {
            label: 'Complete Profile',
            onClick: () => navigate('/employer/profile')
          }
        });
      } else {
        toast.error(isEditing ? 'Failed to update job' : 'Failed to post job');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      job_type: 'Permanent',
      job_title: '',
      short_info: '',
      description: '',
      gender: [],
      locations: [],
      skills: [],
      min_experience: 0,
      max_experience: 0,
      min_age: '',
      max_age: '',
      min_education: [],
      course_subject: '',
      min_salary: '',
      max_salary: '',
      salary_type: 'numeric',
      vacancies: 1,
      last_date: '',
      contact_number: '',
      email: '',
      publish_on: ['job_portal']
    });
    setLocationInput('');
    setSkillInput('');
  };

  const handleSkillInput = (e) => {
    const value = e.target.value;
    if (value.endsWith(',')) {
      const skill = value.slice(0, -1).trim();
      if (skill && !formData.skills.includes(skill)) {
        setFormData({ ...formData, skills: [...formData.skills, skill] });
      }
      setSkillInput('');
    } else {
      setSkillInput(value);
    }
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === 'Enter' && skillInput.trim()) {
      e.preventDefault();
      const skill = skillInput.trim();
      if (!formData.skills.includes(skill)) {
        setFormData({ ...formData, skills: [...formData.skills, skill] });
      }
      setSkillInput('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skillToRemove) });
  };

  const toggleJobStatus = async (jobId, currentStatus) => {
    try {
      await api.put(`/employer/jobs/${jobId}/toggle-status`);
      toast.success(`Job ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const toggleGender = (gender) => {
    const newGenders = formData.gender.includes(gender)
      ? formData.gender.filter(g => g !== gender)
      : [...formData.gender, gender];
    setFormData({ ...formData, gender: newGenders });
  };

  // Location suggestion functions - Search districts and cities
  const searchLocationSuggestions = async (query) => {
    if (query.length < 2) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }
    
    try {
      // Use unified search API that returns both districts and cities
      const response = await api.get(`/location-master/locations/search?q=${encodeURIComponent(query)}&limit=15`);
      setLocationSuggestions(response.data);
      setShowLocationSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching locations:', error);
      setLocationSuggestions([]);
    }
  };

  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationInput(value);
    searchLocationSuggestions(value);
  };

  const selectLocationSuggestion = (suggestion) => {
    // Check for duplicate - now includes city in comparison
    const alreadyExists = formData.locations.some(
      loc => loc.state === suggestion.state && 
             loc.district === suggestion.district && 
             loc.city === suggestion.city
    );
    
    if (alreadyExists) {
      toast.error('This location is already added');
      return;
    }
    
    const newLocation = {
      state: suggestion.state,
      district: suggestion.district,
      city: suggestion.city || suggestion.district  // Fallback to district name if no city
    };
    
    setFormData({ ...formData, locations: [...formData.locations, newLocation] });
    setLocationInput('');
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  const addLocation = (e) => {
    if (e.key === 'Enter' && locationInput.trim()) {
      e.preventDefault();
      if (locationSuggestions.length > 0) {
        selectLocationSuggestion(locationSuggestions[0]);
      }
      setShowLocationSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowLocationSuggestions(false);
    }
  };

  const removeLocation = (index) => {
    const newLocations = formData.locations.filter((_, i) => i !== index);
    setFormData({ ...formData, locations: newLocations });
  };

  const viewJobDetails = (job) => {
    setSelectedJob(job);
    setShowViewDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status, active) => {
    if (status === 'pending') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pending Approval</span>;
    }
    if (status === 'approved' && active) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>;
    }
    if (status === 'approved' && !active) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">Inactive</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected</span>;
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  const downloadCV = (cvPath) => {
    if (!cvPath) {
      toast.error('No CV uploaded');
      return;
    }
    const url = getImageUrl(cvPath);
    window.open(url, '_blank');
  };

  return (
    <div>
      {/* Profile Completion Alert */}
      {!profileCompleted && (
        <Alert className="mb-6 border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Profile Incomplete:</strong> {profileMessage || 'Please complete your company profile to post jobs and add HR executives.'}
            <Button 
              variant="link" 
              className="text-amber-700 underline ml-2 p-0 h-auto"
              onClick={() => navigate('/employer/profile')}
            >
              Complete Profile Now →
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Manage Jobs</h1>
          <p className="text-sm text-slate-600 mt-1">
            {isHR ? 'View and manage jobs you have posted' : 'View and manage all job postings'}
          </p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { if (open && !profileCompleted) { handlePostNewJob(); return; } setShowDialog(open); if (!open) { resetForm(); setSelectedJob(null); setIsEditing(false); setEditingJobId(null); } }}>
          <DialogTrigger asChild>
            <Button 
              data-testid="post-job-btn" 
              className={profileCompleted ? "bg-teal-600 hover:bg-teal-700" : "bg-slate-400 hover:bg-slate-500"}
              onClick={(e) => { if (!profileCompleted) { e.preventDefault(); handlePostNewJob(); } }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Post New Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Edit className="w-5 h-5 text-blue-600" />
                    Edit Job
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 text-teal-600" />
                    Post New Job
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Job Type <span className="text-red-500">*</span></label>
                  <Select value={formData.job_type} onValueChange={(value) => setFormData({...formData, job_type: value})}>
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue placeholder="Select job type" />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Job Designation <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="job-title-input"
                    value={formData.job_title}
                    onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                    placeholder="e.g., Software Developer, HR Manager"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Short info about job <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="job-short-info-input"
                    value={formData.short_info}
                    onChange={(e) => setFormData({...formData, short_info: e.target.value})}
                    placeholder="Brief summary of the job position"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Roles and Responsibilities <span className="text-red-500">*</span></label>
                  <Textarea
                    data-testid="job-description-textarea"
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed roles and responsibilities for this position..."
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Gender (Multiple Selection) <span className="text-red-500">*</span></label>
                  <div className="flex gap-4">
                    {['Male', 'Female', 'Transgender'].map((gender) => (
                      <label key={gender} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.gender.includes(gender)}
                          onCheckedChange={() => toggleGender(gender)}
                          data-testid={`gender-${gender.toLowerCase()}-checkbox`}
                        />
                        <span className="text-sm">{gender}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Required Skills <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="skills-input"
                    placeholder="Type a skill and press Enter or comma to add"
                    value={skillInput}
                    onChange={handleSkillInput}
                    onKeyDown={handleSkillKeyDown}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Example: Python, React, MongoDB - Press Enter or use comma (,) to add each skill. <span className="text-red-500">At least one skill is required.</span>
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                      >
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)} className="hover:text-purple-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Minimum Age Limit</label>
                  <Input
                    data-testid="min-age-input"
                    type="number"
                    placeholder="e.g., 18"
                    value={formData.min_age}
                    onChange={(e) => setFormData({...formData, min_age: e.target.value})}
                    min="18"
                    max="70"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty if no minimum age restriction</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Maximum Age Limit</label>
                  <Input
                    data-testid="max-age-input"
                    type="number"
                    placeholder="e.g., 35"
                    value={formData.max_age}
                    onChange={(e) => setFormData({...formData, max_age: e.target.value})}
                    min="18"
                    max="70"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty if no maximum age restriction</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Qualification (Multiple Selection) <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-3 gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                    {educationOptions.map((edu) => (
                      <label key={edu} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.min_education.includes(edu)}
                          onCheckedChange={(checked) => handleEducationChange(edu, checked)}
                          data-testid={`edu-${edu.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <span className="text-sm">{edu}</span>
                      </label>
                    ))}
                  </div>
                  {formData.min_education.length > 0 && (
                    <p className="text-xs text-teal-600 mt-2">
                      Selected: {formData.min_education.join(', ')}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Course/Subject <span className="text-slate-400">(Optional)</span></label>
                  <Input
                    data-testid="course-subject-input"
                    type="text"
                    placeholder="e.g., COMPUTER SCIENCE, MECHANICAL, COMMERCE"
                    value={formData.course_subject || ''}
                    onChange={(e) => setFormData({...formData, course_subject: e.target.value.toUpperCase()})}
                    style={{textTransform: 'uppercase'}}
                  />
                  <p className="text-xs text-slate-500 mt-1">e.g., COMPUTER SCIENCE, MECHANICAL ENGINEERING, COMMERCE, ARTS</p>
                </div>
                
                {/* Location Section with guidance */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Job Locations <span className="text-red-500">*</span></label>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700">
                        <strong>Tip:</strong> Type city or district name to search. 
                        Example: Type Hosur, Chennai, Coimbatore, Whitefield etc.
                        Supports cities and districts from Tamil Nadu, Karnataka, Kerala, Andhra Pradesh, Telangana, Puducherry.
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      ref={locationInputRef}
                      data-testid="job-location-input"
                      placeholder="Type city or district name (e.g., Hosur, Chennai, Whitefield)"
                      value={locationInput}
                      onChange={handleLocationInputChange}
                      onKeyDown={addLocation}
                    />
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {locationSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none border-b border-slate-100 last:border-b-0"
                            onClick={() => selectLocationSuggestion(suggestion)}
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <div className="flex-1">
                                <span className="text-sm">{suggestion.display}</span>
                                {suggestion.type === 'city' && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">City</span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.locations.map((loc, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                      >
                        <MapPin className="w-3 h-3" />
                        {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : loc.district}, {loc.state}
                        <button type="button" onClick={() => removeLocation(index)} className="hover:text-teal-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Min Experience (years) <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="min-experience-input"
                    type="number"
                    value={formData.min_experience}
                    onChange={(e) => setFormData({...formData, min_experience: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Experience (years) <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="max-experience-input"
                    type="number"
                    value={formData.max_experience}
                    onChange={(e) => setFormData({...formData, max_experience: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>
                
                {/* Salary Section - New Logic */}
                <div>
                  <label className="block text-sm font-medium mb-1">Min Salary (₹) <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="min-salary-input"
                    type="number"
                    value={formData.min_salary}
                    onChange={(e) => setFormData({...formData, min_salary: parseInt(e.target.value) || ''})}
                    placeholder="Enter minimum salary"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Max Salary</label>
                  <Select 
                    value={formData.salary_type} 
                    onValueChange={(val) => {
                      if (val === 'numeric') {
                        setFormData({...formData, salary_type: 'numeric', max_salary: ''});
                      } else {
                        setFormData({...formData, salary_type: val, max_salary: ''});
                      }
                    }}
                  >
                    <SelectTrigger data-testid="max-salary-type-select">
                      <SelectValue placeholder="Select max salary type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="numeric">Enter Amount</SelectItem>
                      <SelectItem value="as_per_norms">As Per Norms</SelectItem>
                      <SelectItem value="as_per_experience">As Per Experience</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.salary_type === 'numeric' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Salary Amount (₹)</label>
                    <Input
                      data-testid="max-salary-input"
                      type="number"
                      value={formData.max_salary}
                      onChange={(e) => setFormData({...formData, max_salary: parseInt(e.target.value) || ''})}
                      placeholder="Enter maximum salary"
                    />
                  </div>
                )}
                
                {formData.salary_type !== 'numeric' && formData.salary_type && (
                  <div>
                    <label className="block text-sm font-medium mb-1 text-transparent">Info</label>
                    <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-700">
                        Max: <strong>{formData.salary_type === 'as_per_norms' ? 'As Per Norms' : 'As Per Experience'}</strong>
                      </p>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-1">Vacancies <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="vacancies-input"
                    type="number"
                    value={formData.vacancies}
                    onChange={(e) => setFormData({...formData, vacancies: parseInt(e.target.value) || 1})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Date to Apply <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="last-date-input"
                    type="date"
                    value={formData.last_date}
                    onChange={(e) => setFormData({...formData, last_date: e.target.value})}
                    required
                  />
                </div>
                
                {/* Contact Info - Either one mandatory */}
                <div className="col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 mb-2">
                    <strong>Note:</strong> Either Contact Number or Email is mandatory
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Number</label>
                      <Input
                        data-testid="contact-number-input"
                        type="tel"
                        value={formData.contact_number}
                        onChange={(e) => setFormData({...formData, contact_number: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <Input
                        data-testid="job-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Email address"
                      />
                    </div>
                  </div>
                </div>

                {/* Publishing Options */}
                <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="block text-sm font-medium mb-2">I agree to publish this job on:</label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="publish-job-portal"
                        data-testid="publish-job-portal"
                        checked={true}
                        disabled={true}
                        className="opacity-70"
                      />
                      <label htmlFor="publish-job-portal" className="text-sm text-slate-700">
                        Job Portal <span className="text-xs text-slate-500">(Default)</span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="publish-youtube"
                        data-testid="publish-youtube"
                        checked={formData.publish_on.includes('youtube')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({...formData, publish_on: [...formData.publish_on, 'youtube']});
                          } else {
                            setFormData({...formData, publish_on: formData.publish_on.filter(p => p !== 'youtube')});
                          }
                        }}
                      />
                      <label htmlFor="publish-youtube" className="text-sm text-slate-700">YouTube</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="publish-whatsapp"
                        data-testid="publish-whatsapp"
                        checked={formData.publish_on.includes('whatsapp')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({...formData, publish_on: [...formData.publish_on, 'whatsapp']});
                          } else {
                            setFormData({...formData, publish_on: formData.publish_on.filter(p => p !== 'whatsapp')});
                          }
                        }}
                      />
                      <label htmlFor="publish-whatsapp" className="text-sm text-slate-700">WhatsApp</label>
                    </div>
                  </div>
                </div>
              </div>
              <Button data-testid="submit-job-btn" type="submit" className={`w-full ${isEditing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-teal-600 hover:bg-teal-700'}`}>
                {isEditing ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Job
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Post Job
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Jobs Grid Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-16 text-center font-semibold">Sl.No</TableHead>
                <TableHead className="font-semibold">Date of Post</TableHead>
                <TableHead className="font-semibold">Job Designation</TableHead>
                <TableHead className="font-semibold">Locations</TableHead>
                <TableHead className="font-semibold">Posted By</TableHead>
                <TableHead className="font-semibold">Last Date</TableHead>
                <TableHead className="text-center font-semibold">Applications</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="text-center font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No jobs posted yet</p>
                    <p className="text-sm text-slate-500 mt-1">Click &quot;Post New Job&quot; to create your first job posting</p>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job, index) => (
                  <TableRow key={job.id} className="hover:bg-slate-50">
                    <TableCell className="text-center font-medium">{index + 1}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(job.created_at)}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{job.job_title}</div>
                      <div className="text-xs text-slate-500">{job.job_type}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {job.locations?.slice(0, 2).map((loc, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-xs">
                            <MapPin className="w-3 h-3" />
                            {loc.district || loc.city}
                          </span>
                        ))}
                        {job.locations?.length > 2 && (
                          <span className="text-xs text-slate-500">+{job.locations.length - 2} more</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-700">{job.posted_by_name || 'Main Employer'}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(job.last_date)}</TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => fetchApplicants(job)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors cursor-pointer"
                        data-testid={`view-applicants-${job.id}`}
                      >
                        <Users className="w-3 h-3" />
                        {job.application_count || 0}
                      </button>
                    </TableCell>
                    <TableCell>{getStatusBadge(job.status, job.active)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewJobDetails(job)}
                          data-testid={`view-job-${job.id}`}
                          className="text-slate-600 hover:text-teal-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {/* Edit button - only for pending/rejected jobs */}
                        {(job.status === 'pending' || job.status === 'rejected') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditJob(job)}
                            data-testid={`edit-job-${job.id}`}
                            className="text-blue-600 hover:text-blue-700"
                            title="Edit Job"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {job.status === 'approved' && (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={job.active}
                              onCheckedChange={() => toggleJobStatus(job.id, job.active)}
                              data-testid={`toggle-job-${job.id}`}
                            />
                            <span className="text-xs text-slate-500">{job.active ? 'Active' : 'Inactive'}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* View Job Details Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Job Details
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{selectedJob.job_title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{selectedJob.job_type}</p>
                </div>
                {getStatusBadge(selectedJob.status, selectedJob.active)}
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-xs text-slate-500">Posted On</label>
                  <p className="font-medium">{formatDate(selectedJob.created_at)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Last Date to Apply</label>
                  <p className="font-medium">{formatDate(selectedJob.last_date)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Posted By</label>
                  <p className="font-medium">{selectedJob.posted_by_name || 'Main Employer'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Applications Received</label>
                  <p className="font-medium text-blue-600">{selectedJob.application_count || 0}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Experience</label>
                  <p className="font-medium">{selectedJob.min_experience} - {selectedJob.max_experience} years</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Salary Range</label>
                  <p className="font-medium">{formatSalary(selectedJob)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Vacancies</label>
                  <p className="font-medium">{selectedJob.vacancies}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Gender</label>
                  <p className="font-medium">{selectedJob.gender?.join(', ') || 'All'}</p>
                </div>
                {selectedJob.min_education && (
                  <div>
                    <label className="text-xs text-slate-500">Qualification</label>
                    <p className="font-medium">{selectedJob.min_education}</p>
                  </div>
                )}
                {(selectedJob.min_age || selectedJob.max_age) && (
                  <div>
                    <label className="text-xs text-slate-500">Age Limit</label>
                    <p className="font-medium">
                      {selectedJob.min_age && selectedJob.max_age 
                        ? `${selectedJob.min_age} - ${selectedJob.max_age} years`
                        : selectedJob.min_age 
                          ? `Min ${selectedJob.min_age} years`
                          : `Max ${selectedJob.max_age} years`}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-slate-500">Locations</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedJob.locations?.map((loc, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      <MapPin className="w-3 h-3" />
                      {loc.district || loc.city}, {loc.state}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500">Short info about job</label>
                <p className="mt-1 text-slate-700">{selectedJob.short_info}</p>
              </div>

              <div>
                <label className="text-xs text-slate-500">Roles and Responsibilities</label>
                <p className="mt-1 text-slate-700 whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-xs text-slate-500">Contact Number</label>
                  <p className="font-medium">{selectedJob.contact_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Email</label>
                  <p className="font-medium">{selectedJob.email || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Applicants List Dialog */}
      <Dialog open={showApplicantsDialog} onOpenChange={setShowApplicantsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Applicants for {selectedJob?.job_title}
            </DialogTitle>
          </DialogHeader>
          
          {loadingApplicants ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
              <p className="text-slate-600">Loading applicants...</p>
            </div>
          ) : applicants.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">No applicants yet</p>
              <p className="text-sm text-slate-500 mt-1">Applications will appear here when candidates apply</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">{applicants.length} applicant(s) found</p>
              
              {applicants.map((applicant, index) => (
                <Card key={applicant.id || index} className="p-4 border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      {applicant.profile?.profile_picture ? (
                        <img 
                          src={getImageUrl(applicant.profile.profile_picture)} 
                          alt={applicant.profile?.full_name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">
                            {applicant.profile?.full_name || 'Unknown'}
                          </h3>
                          <p className="text-sm text-slate-600">
                            Applied on {formatDate(applicant.applied_at)}
                          </p>
                        </div>
                        {applicant.profile?.cv && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadCV(applicant.profile.cv)}
                            className="text-teal-600 border-teal-600 hover:bg-teal-50"
                            data-testid={`download-cv-${index}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download CV
                          </Button>
                        )}
                      </div>
                      
                      <div className="mt-3 grid md:grid-cols-2 gap-2">
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {applicant.profile?.mobile || 'N/A'}
                        </p>
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {applicant.profile?.email || 'N/A'}
                        </p>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-3">
                        {applicant.profile?.highest_qualification && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                            <GraduationCap className="w-3 h-3" />
                            {applicant.profile.highest_qualification}
                          </span>
                        )}
                        {applicant.profile?.total_experience && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs">
                            <Briefcase className="w-3 h-3" />
                            {applicant.profile.total_experience} years exp
                          </span>
                        )}
                        {applicant.profile?.current_location?.city && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                            <MapPin className="w-3 h-3" />
                            {applicant.profile.current_location.city}
                          </span>
                        )}
                      </div>
                      
                      {applicant.profile?.key_skills && applicant.profile.key_skills.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-500 mb-1">Skills:</p>
                          <div className="flex flex-wrap gap-1">
                            {applicant.profile.key_skills.slice(0, 5).map((skill, i) => (
                              <span key={i} className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                            {applicant.profile.key_skills.length > 5 && (
                              <span className="text-xs text-slate-500">+{applicant.profile.key_skills.length - 5} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobManagement;
